// @/pages/Home/Home.tsx
import { useEffect, useState } from "react";
import HomeDashboard from "./HomeDashboard";
import { startReauthRedirect } from "@/authConfig";

import DashboardLoader from "@/components/DashboardLoader/DashboardLoader";
import { useDashboardLoader } from "@/components/DashboardLoader/useDashboardLoader";

import { getDashboardSummary, getActivityLog } from "@/api/dashboardApi";
import type { DashboardSummaryDto } from "@/types/dashboard";

export default function Home() {
  // ----------------------
  // STATE
  // ----------------------
  const [dashboard, setDashboard] = useState<DashboardSummaryDto | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);


  const FEATURES = {
    dashboardSummary: true,
    insights: false,        // start false
  };


  // ----------------------
  // LOADER CONFIG
  // ----------------------
  const loader = useDashboardLoader([
    { id: "summary", label: "Loading Resource Summary…", done: false },
    { id: "activity", label: "Loading Activity Log…", done: false },
  ]);

  // ----------------------
  // MAIN DASHBOARD FETCH
  // ----------------------
  useEffect(() => {
    if (!FEATURES.dashboardSummary) {
      return;
    }

    const t0 = performance.now();
    getDashboardSummary()
      .then((data) => {
        setDashboard(data);
      })
      .catch(async (err: any) => {
        const status = err?.response?.status;
        if (status === 401) {
          setAuthError("Your session appears to have expired. Redirecting to sign in...");
          await startReauthRedirect(err);
          return;
        }

        setAuthError(err?.response?.data?.error || err?.message || "Failed to load dashboard summary.");
      })
      .finally(() => {
        // Always release the loader, even on auth or API failures.
        loader.markDone("summary");
        loader.markDone("activity");
        console.log("getDashboardSummary ms:", Math.round(performance.now() - t0));
    });
  }, []);

  // ----------------------
  // REFRESH ACTIVITY LOG
  // ----------------------
  const refreshActivityLog = async () => {
    if (!dashboard) return;
    
    try {
      console.log('[refreshActivityLog] Fetching activity log...');
      const activityLog = await getActivityLog();
      console.log('[refreshActivityLog] Received activity log:', activityLog?.length, 'items');
      
      // Only update activity log, preserve all other dashboard data
      setDashboard(prev => {
        if (!prev) return null;
        return {
          ...prev,
          recentActivity: activityLog
        };
      });
    } catch (error) {
      console.error("Failed to refresh activity log:", error);
    }
  };

  // ----------------------
  // HIDE LOADER WHEN ALL DONE
  // ----------------------
  useEffect(() => {
    if (loader.allDone) {
      loader.hide();
    }
  }, [loader.allDone]);

  // ----------------------
  // RENDER
  // ----------------------
  return (
    <>
      {/* GLOBAL DASHBOARD LOADER */}
      <DashboardLoader visible={loader.visible} events={loader.events} />
      {/* MAIN CONTENT */}
      {authError && (
        <div className="alert alert-warning mx-3 mt-3" role="alert">
          <div className="fw-semibold mb-1">Authentication required</div>
          <div className="mb-2">{authError}</div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => void startReauthRedirect("manual-home-retry")}
          >
            Sign in again
          </button>
        </div>
      )}
      {dashboard && (
        <>
          <HomeDashboard data={dashboard} onRefreshActivityLog={refreshActivityLog} />
        </>
      )}
    </>
  );
}